import { useOrganizationService } from "../../../../services";
import { useJwt } from "../../../../helpers/common";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import { HiCheckCircle, HiInformationCircle, HiLink, HiSparkles } from "react-icons/hi";
import { SiJira } from "react-icons/si";
import toast from 'react-hot-toast';
import CustomButton from '../../../../components/common/CustomButton';
import FormInput from '../../../../components/common/FormInput';
import useIntegrationStatus from "../../../../hooks/useIntegrationStatus";
import JiraSettingsSkeleton from "./JiraSettingsSkeleton";
import UnsavedChangesIndicator from "../../../../components/common/UnsavedChangesIndicator";

interface JiraSettingsProps {
  handleClose: () => void;
  handleActivationCallback?: () => void;
}

const JiraSettings: React.FC<JiraSettingsProps> = ({ handleClose, handleActivationCallback }) => {
  const navigate = useNavigate();
  const jwtData: any = useJwt();
  const org_id = jwtData.org_id;
  const { jiraSettingsLoading, jiraSettings } = useSelector((state: any) => state.organization);
  const { saveJiraSettings, getJiraSettings } = useOrganizationService();
  const { isAnyOtherIntegrationActive, isIntegrationActive: checkIntegrationActive } = useIntegrationStatus();
  const isJiraActive = checkIntegrationActive('jira');

  const [step, setStep] = useState(1);
  const [payload, setPayload] = useState({
    username: '',
    api_token: '',
    base_url: '',
    project_id: '',
    status_stages: '',
    is_active: false
  });
  const [initialValues, setInitialValues] = useState({
    username: '',
    api_token: '',
    base_url: '',
    project_id: '',
    status_stages: '',
    is_active: false
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    getJiraSettings().then((res: any) => {
      const data = res?.data || {};
      const newPayload = {
        username: data.username || '',
        api_token: data.api_token || '',
        base_url: data.base_url || '',
        project_id: data.project_id || '',
        status_stages: data.status_stages || '',
        is_active: Boolean(data.is_active)
      };
      setPayload(newPayload);
      setInitialValues(newPayload);

      // If authentication failed, show step 1 to display error message
      if (data.is_authenticated === false) {
        setStep(1);
      } else if (newPayload.is_active) {
        setStep(2);
      } else {
        setStep(1);
      }
      setIsInitialized(true);
    });
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (jiraSettingsLoading) return false;

    const normalizeString = (val: any) => (val === null || val === undefined ? '' : String(val).trim());
    const normalizeBool = (val: any) => Boolean(val);

    return (
      normalizeString(payload.username) !== normalizeString(initialValues.username) ||
      normalizeString(payload.api_token) !== normalizeString(initialValues.api_token) ||
      normalizeString(payload.base_url) !== normalizeString(initialValues.base_url) ||
      normalizeBool(payload.is_active) !== normalizeBool(initialValues.is_active)
    );
  }, [payload, initialValues, jiraSettingsLoading]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPayload({ ...payload, [name]: value });
  };

  const handleConnect = () => {
    var filter = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!payload.username) {
      toast.error("Please Add Username!", { duration: 2000 });
      return;
    }
    if (!filter.test(payload.username)) {
      toast.error("Please correct Username. It should be an email address!", { duration: 2000 });
      return;
    }
    if (!payload.api_token) {
      toast.error("Please Add API Token!", { duration: 2000 });
      return;
    }
    if (!payload.base_url) {
      toast.error("Please Add Base URL!", { duration: 2000 });
      return;
    }
    // if (isAnyOtherIntegrationActive("jira")) {
    //   toast.error("Only one integration can be active at a time.", { duration: 2000 });
    //   return;
    // }

    const newPayload: any = { ...payload, is_active: true };
    setIsConnecting(true);

    if (initialValues.api_token && payload.api_token !== initialValues.api_token) {
      newPayload.token_changed = true;
    }

    saveJiraSettings(org_id, newPayload)
      .then(() => {
        setInitialValues(newPayload);
        setPayload(newPayload);
        setStep(2);
        handleActivationCallback?.();
      })
      .catch((error: any) => {
        console.error("Failed to connect:", error);
      })
      .finally(() => {
        setIsConnecting(false);
      });
  };

  const handleDisconnect = () => {
    const newPayload = { ...payload, is_active: false };
    setIsDisconnecting(true);
    saveJiraSettings(org_id, newPayload)
      .then(() => {
        setInitialValues(newPayload);
        setPayload(newPayload);
        setStep(1);
      })
      .finally(() => {
        setIsDisconnecting(false);
      });
  };

  const jiraWebhookAdminUrl = payload?.base_url ? `${(payload.base_url)}/plugins/servlet/webhooks` : "";
  const integrationCallbackUrl = `https://api.cadencepro.ai/jira/integration?id=${org_id}`;

  const isProcessing = jiraSettings?.processing_states === true;
  const isNotAuthenticated = jiraSettings?.is_authenticated === false;

  return (
    <div className="bg-tertiary-light dark:bg-tertiary-dark w-full h-full relative flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5 dark:bg-tertiary-dark z-40 shadow-lg rounded-b-2xl shrink-0">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col min-w-0 max-w-full">
            <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
              Jira Integration
            </div>
            <a
              href="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/documents/CadenceJIRASetupGuide.pdf"
              target="_blank"
              rel="noreferrer"
              className="flex flex-row items-center gap-2 mt-2 text-light-text-tertiary dark:text-white/60 text-sm hover:text-sky-500 dark:hover:text-sky-200 transition-colors duration-200"
            >
              <HiLink className="w-4 h-4" />
              Jira Setup Guide
            </a>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white
              hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
              onClick={handleClose}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {jiraSettingsLoading || isConnecting || isDisconnecting ? (
        <JiraSettingsSkeleton step={isConnecting ? 2 : isDisconnecting ? 1 : (jiraSettings?.is_authenticated === false ? 1 : (isJiraActive || jiraSettings?.is_active ? 2 : 1))} />
      ) : (
        <div className="p-4 md:px-8 md:py-4 flex-1 overflow-y-auto">
          {/* Processing State Notification Banner */}
          {isProcessing && (
            <div className="mb-6 p-4 border border-blue-400 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-600 rounded-lg">
              <div className="flex items-center">
                <HiInformationCircle className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  We are processing at our end, we'll notify you once the integration is ready to use.
                </p>
              </div>
            </div>
          )}

          {/* Authentication Error Banner */}
          {isNotAuthenticated && jiraSettings?.username && jiraSettings?.api_token && jiraSettings?.base_url && (
            <div className="mb-6 p-4 border border-red-400 bg-red-50 dark:bg-red-900/30 dark:border-red-600 rounded-lg">
              <div className="flex items-center">
                <HiInformationCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                <p className="text-sm text-red-700 dark:text-red-300">
                  Authentication failed. Please check your username, API token, and base URL.
                </p>
              </div>
            </div>
          )}

          {step === 1 ? (
            <div className="space-y-6 max-w-3xl">
              {/* Main Info Card */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 shadow-sm">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 mt-2">
                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <SiJira className="w-8 h-8 text-[#0052CC] dark:text-[#0052CC]" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                      Connect your Jira account
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                      Integrate Cadence Pro with Jira to sync issues, track progress, and manage projects directly within your workflows.
                    </p>
                  </div>
                </div>

                {/* Features List */}
                <div className="mb-6 pl-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    What you'll get:
                  </h3>
                  <div className="space-y-2.5">
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Issue Sync</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">View and manage your Jira issues</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Project Tracking</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">Track project progress and status updates automatically</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Development Insights</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">Gain insights into development velocity and team performance</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Input Section */}
                <div className="mt-6 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-6">
                  <FormInput
                    id="username"
                    name="username"
                    label={
                      <span className="flex">
                        Username/Email
                        <span className="ml-1 text-red-500">*</span>
                      </span>
                    }
                    type="text"
                    value={payload.username}
                    onChange={handleOnChange}
                    placeholder="Enter username or email"
                    disabled={isProcessing}
                  />

                  <FormInput
                    id="api_token"
                    name="api_token"
                    label={
                      <span className="flex">
                        API Token
                        <span className="ml-1 text-red-500">*</span>
                      </span>
                    }
                    type="password"
                    value={payload.api_token}
                    onChange={handleOnChange}
                    placeholder="Enter API token"
                    disabled={isProcessing}
                  />

                  <div>
                    <FormInput
                      id="base_url"
                      name="base_url"
                      label={
                        <span className="flex">
                          Base URL
                          <span className="ml-1 text-red-500">*</span>
                        </span>
                      }
                      type="text"
                      value={payload.base_url}
                      onChange={handleOnChange}
                      placeholder="https://your-domain.atlassian.net"
                      disabled={isProcessing || !!initialValues.base_url}
                    />
                    {!!initialValues.base_url && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
                        Please contact our team to help you with the base url change.
                      </p>
                    )}
                  </div>
                </div>


              </div>




              {/* Info Box */}
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <HiInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                      Required Permissions
                    </p>
                    <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed mb-2">
                      You need to generate an API Token in Jira with appropriate permissions.
                    </p>
                    <a
                      href="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/documents/CadenceJIRASetupGuide.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold inline-flex items-center gap-1 hover:underline"
                    >
                      View Setup Guide <HiLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Connection Status */}
              <div className="flex items-center justify-between px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-sm text-green-800 dark:text-green-200 font-medium">Jira is connected</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CustomButton
                    size="sm"
                    variant="danger"
                    className="px-2"
                    onClick={handleDisconnect}
                  >
                    Disconnect
                  </CustomButton>
                </div>
              </div>

              {/* Connected Details */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-4 bg-white/90 dark:bg-gray-800/60 shadow-sm">
                <div className="text-sm text-light-text-secondary dark:text-white/70">
                  Your organization is connected. These details help you verify the correct account is linked.
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-light-text-tertiary dark:text-white/60">Base URL</span>
                    <span className="font-semibold text-base text-light-text-primary dark:text-white">{payload.base_url || '-'} </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-light-text-tertiary dark:text-white/60">Username</span>
                    <span className="font-semibold text-base text-light-text-primary dark:text-white">{payload.username || '-'} </span>
                  </div>
                </div>
              </div>

              {/* Webhook Configuration */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800/60 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="shrink-0">
                    <HiInformationCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Webhook Configuration</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed">
                      Ensure your Jira webhooks are configured to receive updates.
                    </p>
                  </div>
                </div>

                <div className="mt-2 p-4 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-white/5">
                  <ol className="list-decimal ml-5 space-y-2 text-xs text-light-text-secondary dark:text-white/80">
                    <li>
                      Open Jira Webhooks settings: {jiraWebhookAdminUrl ? (
                        <a href={jiraWebhookAdminUrl} target="_blank" rel="noreferrer" className="text-sky-600 dark:text-sky-300 hover:underline break-all">{jiraWebhookAdminUrl}</a>
                      ) : (
                        <span className="text-light-text-tertiary dark:text-white/60">Add a valid Base Url above</span>
                      )}. You must be a Jira admin.
                    </li>
                    <li>Click "Create a webhook".</li>
                    <li>
                      In the "URL" field, paste: <span className="font-mono text-[10px] break-all px-1 py-0.5 rounded bg-gray-200 dark:bg-white/10">{integrationCallbackUrl}</span>
                    </li>
                    <li>
                      For "JQL" leave it empty to include all issues.
                    </li>
                    <li>
                      Under "Issue" events, select: <span className="font-medium">created</span>, <span className="font-medium">updated</span>, <span className="font-medium">deleted</span>.
                    </li>
                    <li>
                      Under "Issue link" events, select: <span className="font-medium">created</span>, <span className="font-medium">deleted</span>.
                    </li>
                    <li>Save the webhook.</li>
                  </ol>
                </div>
              </div>

              {/* Next Steps Grid */}
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Next Steps: Start using your data</h3>
              <div className="grid grid-cols-1 gap-4">
                {/* Import Projects Box */}
                <div className="rounded-xl border border-blue-200 dark:border-blue-800 p-5 bg-white dark:bg-gray-800/60 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="shrink-0">
                      <HiLink className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Import Projects & Sync Data</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed">
                        Bring your Jira data into Cadence to visualize your roadmap.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Import Projects Action */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-800/60 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Import Projects</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Select specific projects and epics to create your first Strategy Map.
                        </p>
                      </div>
                      <CustomButton
                        size="sm"
                        variant="primary"
                        className="whitespace-nowrap shrink-0"
                        onClick={() => {
                          handleClose();
                          navigate('/#importproject=jira', { state: { openImportDrawer: true, initialIntegration: 'jira', initialTab: 'import' } });
                        }}
                      >
                        Import Projects
                      </CustomButton>
                    </div>

                    {/* Configure State Mapping Action */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-800/60 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Map States</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Align your Jira states with Cadence's progress tracking.
                        </p>
                      </div>
                      <CustomButton
                        size="sm"
                        variant="primary"
                        className="whitespace-nowrap shrink-0"
                        onClick={() => {
                          handleClose();
                          navigate('/#importproject=jira&tab=states');
                        }}
                      >
                        Configure States
                      </CustomButton>
                    </div>

                    {/* Sync Sprints Action */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-800/60 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Sync Sprints</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Keep your sprint cycles in sync for accurate timeline projections.
                        </p>
                      </div>
                      <CustomButton
                        size="sm"
                        variant="primary"
                        className="whitespace-nowrap shrink-0"
                        onClick={() => {
                          handleClose();
                          navigate('/#importproject=jira&tab=sprints');
                        }}
                      >
                        Sync Sprints
                      </CustomButton>
                    </div>
                  </div>
                </div>

                {/* AI Analysis Box */}
                <div className="rounded-xl border border-blue-200 dark:border-blue-800/50 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="shrink-0">
                      <HiSparkles className="w-5 h-5 text-blue-500 hover:text-purple-500 transition-colors duration-200" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">AI Analysis & Insights</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed">
                        Leverage Cadence AI to analyze your projects and get actionable insights.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-lg border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/50 transition-all shadow-sm hover:shadow-md cursor-default group">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Risk Analysis & Recommendations</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">"Analyze why my Epic X is at risk? Provide actionable recommendations to mitigate the risk."</p>
                    </div>

                    <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-lg border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/50 transition-all shadow-sm hover:shadow-md cursor-default group">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Sprint Health & Status</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">"How is my current sprint going? Show me detailed progress and status updates."</p>
                    </div>

                    <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-lg border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/50 transition-all shadow-sm hover:shadow-md cursor-default group">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Epic Status & Timeline</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">"What is the status of my Q4 Epic? Provide a timeline view for this epics."</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-4 items-center justify-between flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700
        bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg shrink-0">
        <CustomButton
          variant="cancel"
          onClick={handleClose}
          className="ml-2"
        >
          {step === 1 ? 'Close' : 'Cancel'}
        </CustomButton>

        <div className="ml-auto flex items-center gap-3">
          <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} />
          {step === 1 && (
            <CustomButton
              onClick={handleConnect}
              disabled={jiraSettingsLoading || !payload.username || !payload.api_token || !payload.base_url || isProcessing || (isNotAuthenticated && !hasUnsavedChanges)}
              isLoading={jiraSettingsLoading}
              title={isAnyOtherIntegrationActive('jira') ? "Only one integration can be active at a time." : ""}
            >
              {jiraSettingsLoading ? 'Connecting…' : 'Connect'}
            </CustomButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default JiraSettings;